import { ErrorHandler, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { getNewRelicAgent, nativeConsole } from '../../config/new-relic';

/**
 * Angular catches errors itself, so they never reach window.onerror and the
 * New Relic agent does not see them. Report them explicitly instead.
 */
@Injectable()
export class NewRelicErrorHandler extends ErrorHandler {
    override handleError(error: unknown): void {
        const reported = this.toError(error);

        // API failures are already logged by HttpBaseService (and recorded as AJAX events)
        if (!this.isHttpFailure(error)) {
            getNewRelicAgent()?.noticeError(reported);
        }

        // Native console: visible in DevTools, but not captured again as a New Relic log
        nativeConsole.error(reported.message, error);
    }

    private isHttpFailure(error: unknown): boolean {
        const unwrapped = this.unwrap(error);

        // HttpBaseService rethrows HttpErrorResponse.message ("Http failure response for ...")
        return (
            unwrapped instanceof HttpErrorResponse ||
            (typeof unwrapped === 'string' &&
                unwrapped.startsWith('Http failure'))
        );
    }

    private unwrap(error: unknown): unknown {
        // Unhandled promise rejections are wrapped by zone.js
        return (error as { rejection?: unknown })?.rejection ?? error;
    }

    private toError(error: unknown): Error {
        const unwrapped = this.unwrap(error);

        if (unwrapped instanceof Error) {
            return unwrapped;
        }
        if (typeof unwrapped === 'string') {
            return new Error(unwrapped);
        }
        // e.g. HttpErrorResponse, which is not an Error subclass
        const message = (unwrapped as { message?: unknown })?.message;
        if (typeof message === 'string') {
            return new Error(message);
        }
        try {
            return new Error(JSON.stringify(unwrapped) ?? String(unwrapped));
        } catch {
            // Circular structures
            return new Error(String(unwrapped));
        }
    }
}
